import { createStore } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { IS_DEVELOPMENT } from '~/modules/config';
import { SearchContext, SearchContextListing, SearchResult, SearchResultListing } from '../../schemas/search-schema-v1';
import { getSection } from '../services/recommendationService';
import { logger } from '../utilities/logger';

export interface ListingRecords {
    isLoading: boolean;
    isEmpty?: boolean;
    isError: boolean;
    errorMessage: string;
    records: SearchResult[];
}

interface RecommendationState extends State {
    sections: Record<string, ListingRecords>;
}

const initialRecordsState = (records = []) => {
    return {
        isLoading: false,
        isError: false,
        errorMessage: '',
        records,
    };
};

const initialState: RecommendationState = {
    sections: {},
};

export type SearchEventContext = SearchContext & SearchContextListing;

export const selectSearchContext = (
    state: RecommendationState,
    sectionId: string,
    listingId: string,
): SearchEventContext => {
    if (sectionId && listingId) {
        let section: SearchResult;
        for (const currentSection of Object.values(state.sections)) {
            const sectionLookup = currentSection.records.filter((s) => s.id === sectionId);
            if (sectionLookup.length === 1) {
                section = sectionLookup[0];
                break;
            }
        }
        // @ts-expect-error * Automated Type Error Fix
        if (section) {
            const listings: SearchResultListing[] = section.listings.filter(
                (l: SearchResultListing) => l.oafId === listingId,
            );
            if (listings && listings.length === 1) {
                const listing = listings[0];
                return {
                    searchQueryUid: section.context.searchQueryUid,
                    queryPipeline: section.context.queryPipeline,
                    documentUri: listing.context.documentUri,
                    documentUrl: listing.context.documentUrl,
                    documentUriHash: listing.context.documentUriHash,
                    documentPosition: listing.context.documentPosition,
                    collection: listing.context.collection,
                    rankingModifier: listing.context.rankingModifier,
                    sourceName: listing.context.sourceName,
                    originLevel1: section.context.originLevel1,
                    originLevel2: section.context.originLevel2,
                    language: section.context.language,
                    duration: section.context.duration,
                    numberOfResults: section.context.numberOfResults,
                } as SearchEventContext;
            }
        }
    }
    // @ts-expect-error * Automated Type Error Fix
    return null;
};

export const recommendationStore = createStore(
    initialState,

    // the modifiers to the state - this could get long...?  this
    // is my main concern about a single store but I'm interested
    // to see how it goes
    (set: SetState<RecommendationState>, get: GetState<RecommendationState>) => ({
        setError(section: string, error: string) {
            set(function setError(draft) {
                logger.error(`Section ${section} has the following error; ${error}`);
                draft.sections[section] = {
                    isLoading: false,
                    isError: true,
                    errorMessage: error,
                    records: [],
                };
                return draft;
            });
        },

        async fetchSection(sectionName: string, tbidToken: string, retry: boolean) {
            const sectionSelector = get().sections[sectionName];
            const hasRecords = sectionSelector?.records?.length > 0;

            if (!hasRecords || retry) {
                try {
                    const results: SearchResult[] = await getSection(sectionName, tbidToken);
                    set(function updateSections(draft: RecommendationState) {
                        // @ts-expect-error * Automated Type Error Fix
                        draft.sections[sectionName] = initialRecordsState(results);
                    });
                } catch (error) {
                    this.setError(sectionName, error);
                }
            }
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
        },
    }),
    'recommendation',
    IS_DEVELOPMENT,
);
